import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
    HomePage,
    DashboardPage,
    RolesPage,
    SapTablesEccPage,
    SapTablesS4Page,
    LocalTablesEccPage,
    LocalTablesS4Page,
    SettingsPage,
    CustomerRequestPage,
    CustomerRequestsPage,
} from '../pages';

import {UserContext} from '.'

// Looking for an icon name?
// look here! https://material-ui.com/components/material-icons/
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFileOutlined';
import SpeedIcon from '@material-ui/icons/SpeedOutlined';
import ListIcon from '@material-ui/icons/ListOutlined';
import PeopleAltIcon from '@material-ui/icons/PeopleAltOutlined';
import LayersIcon from '@material-ui/icons/LayersOutlined';
import SettingsIcon from '@material-ui/icons/Settings';
import { ApplicationRoles } from '../../helpers';

export const NavRoutes = {
    MyDesk: "/",
    Logout: "/logout",
    NotFound: "/notfound",
    NoSettings:"/nosettings",
    Forbidden: "/forbidden",
    NewRequest: "/requests/new",
    EditRequest: "/requests/edit/:requestId",    
    ViewRequest: "/requests/view/:requestId",
    RequestHistory: "/requests/history/:requestId",
    DraftRequests: "/requests/draft",
    RunningRequests: "/requests/running",
    CompletedRequests: "/requests/completed",
    Admin: {
        Dashboard: "/admin",
        Roles: "/admin/roles/all",
        EditRole: "/admin/roles/edit/:roleId",
        AddUserInRole: "/admin/roles/edit/:roleId/adduser",
        EditUserInRole: "/admin/roles/edit/:roleId/edituser/:userKey",
        SapTablesECC: "/admin/tables/sap/ecc",
        SapTablesS4: "/admin/tables/sap/s4",
        LocalTablesECC: "/admin/tables/local/ecc",
        LocalTablesS4: "/admin/tables/local/s4",
        Settings: "/admin/settings"
    }
};

export const NavContext = React.createContext(null);

export const NavProvider = (props) => {
    const history = useHistory();
    const location = useLocation();

    const {
        draftRequests
    } = useContext(UserContext)
    
    const [breadcrumbs, setBreadcrumbs] = useState(JSON.parse(sessionStorage.getItem("breadcrumbs") || "[]"));

    const navigateTo = (route, args) => {
        route = compileRoute(route, args);
        history.push(route);
    }

    const switchTo = (route, args) => {
        route = compileRoute(route, args);
        history.replace(route);
    }

    const getRouteTo = (route, args) => {
        route = compileRoute(route, args);
        return route;
    }

    const compileRoute = (route, args) => {
        if(route.includes(":") && args && args.length > 0) {
            for(let i = 0; i < args.length; i++) {
                let startIndex = route.indexOf(":", 0);
                if(startIndex === -1) break;
                let endIndex = route.indexOf("/", startIndex);
                if(endIndex === -1) endIndex = route.length - 1;
                else endIndex--;
                const token = route.substr(startIndex, (endIndex - startIndex) + 1);
                route = route.replace(token, args[i]);               
            }          
        }

        return route;        
    }

    const setBreadcrumbRoot = (title, path) => {        
        const tokens = [{ title, url: path ? path : location.pathname }];    
        setBreadcrumbs(tokens);
        sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
    }

    const setBreadcrumbToken = (title) => {
        const tokens = [].concat(breadcrumbs);
        const token = tokens.find(x => x.url === location.pathname);
		if(token) {
			token.title = title;
			setBreadcrumbs(tokens);
			sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
		}
    }

    const setBreadcrumbTokens = (tokens) => {
        if(tokens){
            setBreadcrumbs(tokens)
            sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
        }
    }

    const addBreadcrumbToken = (title) => {        
        let tokens = [].concat(breadcrumbs);
        if(tokens.find(x => x.url === location.pathname)) {
            const index = tokens.findIndex(x => x.url === location.pathname);
            tokens = tokens.slice(0, index + 1);
        } else {
            tokens.push({ title, url: location.pathname });
        }

        setBreadcrumbs(tokens);
        sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
    }

    const getCurrentLocation = () => {
        return location;
    }

    const [menuItems, setMenuItems] = useState([]);
    useEffect(() => {

        //TODO: Add user role checking?
        // Add here all menu items
        setMenuItems([
            {
                title: "MyDesk",
                routePath: NavRoutes.MyDesk,
                icon: <SpeedIcon color="primary" />,
                isProtected: true,
                component: HomePage,
                isAdmin: false
            },
            {
                title: "New Request",
                routePath: NavRoutes.NewRequest,
                icon: <InsertDriveFileIcon color="primary" />,
                isProtected: true,
                component: CustomerRequestPage,
                isAdmin: false,
                roles: [ ApplicationRoles.AOFOpener, ApplicationRoles.BossApprover ]
            },
            {
                title: `Draft requests (${draftRequests})`,
                routePath: NavRoutes.DraftRequests,
                icon: <InsertDriveFileIcon color="primary" />,
                isProtected: true,
                component: CustomerRequestsPage,
                isAdmin: false,
                roles: [ ApplicationRoles.AOFOpener, ApplicationRoles.BossApprover]            
            },
            {
                title: "Running requests",
                routePath: NavRoutes.RunningRequests,
                icon: <ListIcon color="primary" />,
                isProtected: true,
                component: CustomerRequestsPage,
                isAdmin: false
            },
            {
                title: "Completed requests",
                routePath: NavRoutes.CompletedRequests,
                icon: <ListIcon color="primary"/>,
                isProtected: true,
                component: CustomerRequestsPage,
                isAdmin: false
            },
            {
                title: "Dashboard",
                routePath: NavRoutes.Admin.Dashboard,
                isProtected: true,
                component: DashboardPage,
                isAdmin: true
            },            
            {
                title: "Roles",
                description: "View and manage user role assignations. Here you can monitor and handle user authorizations.",
                routePath: NavRoutes.Admin.Roles,
                icon: <PeopleAltIcon />,
                isProtected: true,
                component: RolesPage,
                isAdmin: true,
                roles: [ ApplicationRoles.ProcessOwner]
            },
            {
                title: "SAP Data ECC",
                description: "Manage data values coming from SAP ECC. Here you can monitor and handle which data values are available from SAP ECC.",
                routePath: NavRoutes.Admin.SapTablesECC,
                icon: <LayersIcon />,
                isProtected: true,
                component: SapTablesEccPage,
                isAdmin: true,
                roles: [ ApplicationRoles.DataQuality, ApplicationRoles.ProcessOwner]
            },
            {
                title: "SAP Data S4",
                description: "Manage data values coming from SAP S4. Here you can monitor and handle which data values are available from SAP S4.",
                routePath: NavRoutes.Admin.SapTablesS4,
                icon: <LayersIcon />,
                isProtected: true,
                component: SapTablesS4Page,
                isAdmin: true,
                roles: [ ApplicationRoles.DataQuality, ApplicationRoles.ProcessOwner]
            },
            {
                title: "Local Data ECC",
                description: "Manage ECC local data values. Here you can monitor and handle which data values are available.",
                routePath: NavRoutes.Admin.LocalTablesECC,
                icon: <LayersIcon />,
                isProtected: true,
                component: LocalTablesEccPage,
                isAdmin: true,
                roles: [ApplicationRoles.DataQuality, ApplicationRoles.ProcessOwner]
            },
            {
                title: "Local Data S4",
                description: "Manage S4 local data values. Here you can monitor and handle which data values are available.",
                routePath: NavRoutes.Admin.LocalTablesS4,
                icon: <LayersIcon />,
                isProtected: true,
                component: LocalTablesS4Page,
                isAdmin: true,
                roles: [ApplicationRoles.DataQuality, ApplicationRoles.ProcessOwner]
            },
            {
                title: "Settings",
                description: "Application global settings. Here you can configure application globally used settings, SAP defaults.",
                routePath: NavRoutes.Admin.Settings,
                icon: <SettingsIcon />,
                isProtected: true,
                component: SettingsPage,
                isAdmin: true,
                roles: [ ApplicationRoles.DataQuality]
            }
        ]);

    }, [draftRequests]);

    const { Provider } = NavContext;

    return (
        <Provider value={{
            menuItems,
            navigateTo,            
            switchTo,
            getRouteTo,
            breadcrumbs,
            setBreadcrumbRoot,
            setBreadcrumbToken,
            setBreadcrumbTokens,
            addBreadcrumbToken,
            getCurrentLocation
        }}>
            {props.children}
        </Provider>
    );
};